import type { Slot } from "../lib/api.ts";

interface SlotGridProps {
  slots: Slot[];
  selected: string | null; // "HH:mm" of selected slot.start
  onSelect: (slot: Slot) => void;
  loading?: boolean;
  error?: string | null;
  /** If provided, shows a "Find next available date" button in the empty state. */
  onFindNextAvailable?: () => void;
  findNextLoading?: boolean;
  findNextError?: string | null;
}

/** Convert "HH:mm" 24h to "h:mm AM/PM" */
function formatTime(hhmm: string): string {
  const [hStr, m] = hhmm.split(":");
  const h = Number.parseInt(hStr ?? "0", 10);
  if (Number.isNaN(h)) return hhmm;
  const period = h >= 12 ? "PM" : "AM";
  const displayH = h === 0 ? 12 : h > 12 ? h - 12 : h;
  return `${displayH}:${m} ${period}`;
}

export function SlotGrid({
  slots,
  selected,
  onSelect,
  loading,
  error,
  onFindNextAvailable,
  findNextLoading,
  findNextError,
}: SlotGridProps) {
  if (loading) {
    return (
      <div className="flex items-center justify-center h-24 text-gray-500 text-sm animate-pulse">
        Loading slots...
      </div>
    );
  }

  if (error) {
    return <p className="text-red-600 text-sm">{error}</p>;
  }

  const available = slots.filter((s) => s.available);

  if (available.length === 0) {
    return (
      <div className="text-center py-8 text-gray-400">
        <div className="text-3xl mb-2" aria-hidden="true">🗓️</div>
        <p className="text-sm">No available slots on this date.</p>
        {onFindNextAvailable && !findNextError && (
          <button
            type="button"
            onClick={onFindNextAvailable}
            disabled={findNextLoading}
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-[var(--radius)] border-2 transition-all disabled:opacity-60"
            style={{
              borderColor: "var(--accent)",
              color: "var(--accent)",
              backgroundColor: "transparent",
            }}
          >
            {findNextLoading ? (
              <>
                <span className="animate-spin">⟳</span> Searching...
              </>
            ) : (
              <>
                Find next available date <span aria-hidden="true">→</span>
              </>
            )}
          </button>
        )}
        {findNextError && (
          <p className="text-xs mt-3 text-red-500">{findNextError}</p>
        )}
        {!onFindNextAvailable && (
          <p className="text-xs mt-1">Try selecting a different date.</p>
        )}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
      {available.map((slot) => {
        const isSelected = slot.start === selected;
        return (
          <button
            key={slot.start}
            type="button"
            onClick={() => onSelect(slot)}
            className={`py-2 px-3 text-sm font-medium text-center rounded-[var(--radius)] border-2 transition-all ${
              isSelected
                ? "text-white border-[var(--accent)]"
                : "border-gray-200 bg-white text-gray-700 hover:border-[var(--accent)] hover:text-[var(--accent)]"
            }`}
            style={isSelected ? { backgroundColor: "var(--accent)", borderColor: "var(--accent)" } : {}}
          >
            {formatTime(slot.start)}
          </button>
        );
      })}
    </div>
  );
}
